import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";

import { can, handleError } from "src/Common/function";
import { getAvatarUrl, getImageUrl } from "src/Common/common";
import FormTemplateService from "src/Services/API/formtemplate.service";
import Button from "src/Components/Button";
import CreateButton from "src/Components/CreateButton";
import ConfirmDialog from "src/Common/confirmdialog";
import CreateTemplateModal from "./CreateTemplateModal";
import FormTemplateFilterView from "./components/FormTemplateFilterView";
import { FORM_TYPES } from "./components/FormTypeDropdown";
import FormCreateModal from "src/Pages/WO/Components/forms/FormCreateModal";
import UserService from "src/Services/API/user.service";
import AddTemplateModal from "./components/AddTemplateModal";

const getFormTypeLabel = (value) => FORM_TYPES.find((t) => t.value === value)?.label || "";
const FREQUENCIES = [
  { value: 0, label: "One Time" },
  { value: 1, label: "Weekly" },
  { value: 2, label: "Monthly" },
  { value: 3, label: "Quarterly" },
  { value: 4, label: "Semi-Annual" },
  { value: 5, label: "Annual" },
];
const getFrequencyLabel = (value) => FREQUENCIES.find((f) => f.value === Number(value))?.label || "-";

function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        color: active ? "#0058E5" : "#7C829A",
        fontFamily: "OpenSansRegular",
        fontSize: "14px",
        fontWeight: 600,
        lineHeight: "20px",
        letterSpacing: "0.14px",
        textTransform: "capitalize",
      }}
      className="relative py-4 text-center"
    >
      {label}
      {active && <span className="absolute left-0 -bottom-px w-full h-[2px] bg-blueColor" />}
    </button>
  );
}

const TemplateList = ({ hideTabs = false, hideControls = false, customerId = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCustomerContext = customerId !== null && customerId !== undefined;

  const canCreate = can("form:templateCreate");
  const canUpdate = can("form:templateUpdate");
  const canArchive = can("form:templateArchive");
  const canRestore = can("form:templateRestore");
  const canDuplicate = can("form:templateDuplicate");
  const canCreateForm = can("form:create");

  const [tab, setTab] = useState("Active");
  const [templates, setTemplates] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilterType, setAppliedFilterType] = useState("");
  const [appliedFilterCustomerId, setAppliedFilterCustomerId] = useState("");

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [addTemplateModalOpen, setAddTemplateModalOpen] = useState(false);
  const [assignableTemplates, setAssignableTemplates] = useState([]);
  const [selectedTemplateForEdit, setSelectedTemplateForEdit] = useState(null);
  const [formCreateModalOpen, setFormCreateModalOpen] = useState(false);
  const [selectedTemplateForForm, setSelectedTemplateForForm] = useState(null);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [confirmTemplateId, setConfirmTemplateId] = useState(null);

  const fetchTemplates = (isActive, formType = appliedFilterType, filterCustomerId = appliedFilterCustomerId) => {
    const activeParam = isActive !== undefined ? isActive : tab === "Active";
    FormTemplateService.getAll(
      String(activeParam),
      formType !== "" ? formType : "",
      isCustomerContext ? customerId : filterCustomerId !== "" ? filterCustomerId : "",
    )
      .then((response) => {
        if (response.status === 200) {
          setTemplates(response.data);
        }
      })
      .catch((error) => handleError(error));
  };

  useEffect(() => {
    fetchTemplates(hideTabs ? true : tab === "Active", appliedFilterType, appliedFilterCustomerId);
  }, [tab, hideTabs, customerId, appliedFilterType, appliedFilterCustomerId]);

  const handleTabClick = (nextTab) => {
    setTab(nextTab);
    setActionMenuId(null);
  };

  const handleApplyFilter = ({ filterType, customerId: nextCustomerId }) => {
    setAppliedFilterType(filterType);
    setAppliedFilterCustomerId(nextCustomerId || "");
  };

  const handleClearFilter = () => {
    setAppliedFilterType("");
    setAppliedFilterCustomerId("");
  };

  const handleCreate = (data) => {
    const isEditing = selectedTemplateForEdit !== null;
    setCreateModalOpen(false);
    setAddTemplateModalOpen(false);
    navigate("/form-builder", {
      state: {
        mode: "template",
        templateDraft: data,
        templateName: data.name,
        ...(isEditing
          ? {
              templateId: selectedTemplateForEdit.id,
              formSchema: selectedTemplateForEdit.schema ?? null,
            }
          : {}),
        customerId: isCustomerContext ? customerId : undefined,
        from: isCustomerContext ? location.pathname : "/form-templates",
      },
    });
    setSelectedTemplateForEdit(null);
  };

  const handleAssignTemplate = (templateId) => {
    if (!isCustomerContext || !templateId) return;
    FormTemplateService.update(templateId, { customerId })
      .then(() => {
        toast("Template assigned");
        setAddTemplateModalOpen(false);
        fetchTemplates(hideTabs ? true : tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const openAddTemplateModal = () => {
    if (!isCustomerContext) {
      setCreateModalOpen(true);
      return;
    }

    FormTemplateService.getAll("true", "", "")
      .then((response) => {
        if (response.status === 200) {
          setAssignableTemplates(response.data.filter((t) => !t.customer));
        }
        setAddTemplateModalOpen(true);
      })
      .catch((error) => handleError(error));
  };

  const handleArchive = (id) => {
    FormTemplateService.archive(id)
      .then(() => {
        toast("Template archived");
        setActionMenuId(null);
        fetchTemplates(tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const handleRestore = (id) => {
    FormTemplateService.restore(id)
      .then(() => {
        toast("Template restored");
        setActionMenuId(null);
        fetchTemplates(tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const handleEdit = (template) => {
    setSelectedTemplateForEdit(template);
    setCreateModalOpen(true);
    setActionMenuId(null);
  };

  const handleDuplicate = (template) => {
    const currentUser = UserService.getCurrentUser();

    FormTemplateService.duplicate(template.id, currentUser?.id)
      .then(() => {
        toast("Template duplicated");
        setActionMenuId(null);
        fetchTemplates(tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const handlePlusAction = (template) => {
    setSelectedTemplateForForm(template);
    setFormCreateModalOpen(true);
    setActionMenuId(null);
  };

  const requestArchiveRestore = (id) => {
    setConfirmTemplateId(id);
    setActionMenuId(null);
  };

  const handleConfirmArchiveRestore = () => {
    if (confirmTemplateId === null) return;
    if (tab === "Active") {
      handleArchive(confirmTemplateId);
    } else {
      handleRestore(confirmTemplateId);
    }
    setConfirmTemplateId(null);
  };

  return (
    <div className="w-full h-full flex flex-col min-h-0 bg-backgroundColor">
      <ConfirmDialog
        title={tab === "Active" ? "Archive Template" : "Restore Template"}
        description={
          tab === "Active"
            ? "Are you sure you want to archive this template?"
            : "Are you sure you want to restore this template?"
        }
        shown={confirmTemplateId !== null}
        onConfirmClicked={handleConfirmArchiveRestore}
        onCancelClicked={() => setConfirmTemplateId(null)}
      />

      <CreateTemplateModal
        shown={createModalOpen}
        mode={selectedTemplateForEdit ? "edit" : "create"}
        initialData={selectedTemplateForEdit}
        onCancelClicked={() => {
          setCreateModalOpen(false);
          setSelectedTemplateForEdit(null);
        }}
        onSubmitClicked={handleCreate}
      />
      <AddTemplateModal
        shown={addTemplateModalOpen}
        templates={assignableTemplates}
        onClose={() => setAddTemplateModalOpen(false)}
        onAssignTemplate={handleAssignTemplate}
        onCreateTemplate={() => {
          setAddTemplateModalOpen(false);
          setCreateModalOpen(true);
        }}
      />
      <FormCreateModal
        shown={formCreateModalOpen}
        onClose={() => {
          setFormCreateModalOpen(false);
          setSelectedTemplateForForm(null);
        }}
        initialTemplateId={selectedTemplateForForm?.id ?? null}
        initialTemplateSchema={selectedTemplateForForm?.schema ?? null}
        initialFormType={selectedTemplateForForm?.formType ?? null}
        initialFrequency={
          selectedTemplateForForm?.frequency !== null && selectedTemplateForForm?.frequency !== undefined
            ? String(selectedTemplateForForm.frequency)
            : ""
        }
      />

      {!hideControls && (
        <div className="w-full flex items-center justify-between px-[18px] border-b border-borderColor">
          <div className="flex items-center gap-6">
            {!hideTabs && (
              <>
                <TabButton label="Manage" active={tab === "Active"} onClick={() => handleTabClick("Active")} />
                <TabButton label="Archived" active={tab === "Archived"} onClick={() => handleTabClick("Archived")} />
              </>
            )}
          </div>
          <div className="flex items-center gap-8">
            <Button onClicked={() => setFilterOpen(!filterOpen)} title="Filter" isHighlighted={filterOpen} />
            {canCreate && <CreateButton onClicked={() => setCreateModalOpen(true)} title="CREATE" />}
          </div>
        </div>
      )}

      <div className={`flex-1 min-h-0 flex flex-col ${hideControls ? "" : "px-7 mt-5"}`}>
        {!hideControls && filterOpen && (
          <div className="relative z-20">
            <FormTemplateFilterView onApply={handleApplyFilter} onClear={handleClearFilter} />
          </div>
        )}

        <div
          className="w-full mt-3 mb-3 flex-1 min-h-0 flex flex-col overflow-hidden"
          style={{
            borderRadius: "12px",
            border: "1px solid #E5E8F3",
            background: "#FFF",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.00), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          }}
        >
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                <tr
                  style={{ borderBottom: "1px solid #E5E8F3", background: "#F8F9FE" }}
                  className="[&>th]:border-r [&>th]:border-borderColor [&>th:last-child]:border-r-0"
                >
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[200px] h-[44px] text-[#5F6477]">
                      Template Name
                    </div>
                  </th>
                  <th className="w-min-[200px] w-full">
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 h-[44px] text-[#5F6477]">
                      Description
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[200px] h-[44px] text-[#5F6477]">
                      Type
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[180px] h-[44px] text-[#5F6477]">
                      Frequency
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[180px] h-[44px] text-[#5F6477]">
                      Created by
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[180px] h-[44px] text-[#5F6477]">
                      Customer
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center justify-center w-[140px] h-[44px] text-[#5F6477]">
                      Date Created
                    </div>
                  </th>
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[20px] h-[44px] text-[#5F6477]"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr
                    key={template.id}
                    className="border-b border-borderColor hover:bg-[#FAFAFB] [&>td]:border-r [&>td]:border-borderColor [&>td:last-child]:border-r-0"
                  >
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] word-break-break-word">
                        {template.name}
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] word-break-break-word">
                        {template.description}
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                        {getFormTypeLabel(template.formType)}
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                        {template.frequency !== null && template.frequency !== undefined
                          ? getFrequencyLabel(template.frequency)
                          : "-"}
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] flex items-center gap-2">
                        {template.createdBy?.avatar && (
                          <img
                            src={getAvatarUrl(template.createdBy.avatar)}
                            className="w-[20px] h-[20px] rounded-[10px] object-contain"
                          />
                        )}
                        <span>{template.createdBy?.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] word-break-break-word">
                        {template.customer?.companyName || "-"}
                      </div>
                    </td>
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] text-center">
                        {moment(template.createdAt).format("MM/DD/YYYY")}
                      </div>
                    </td>
                    <td className="relative">
                      <img
                        onClick={() => setActionMenuId(actionMenuId === template.id ? null : template.id)}
                        className="cursor-pointer"
                        src={getImageUrl("/images/common/options.png")}
                        alt="Options"
                      />
                      {actionMenuId === template.id && (
                        <OutsideClickHandler onOutsideClick={() => setActionMenuId(null)}>
                          <div className="flex flex-col items-center py-1 gap-2 absolute top-[24px] right-0 w-[30px] border-[1px] rounded-[6px] border-borderColor bg-white z-20 shadow-md">
                            {tab === "Active" && (
                              <>
                                {canCreateForm && (
                                  <img
                                    onClick={() => handlePlusAction(template)}
                                    className="w-[15px] h-[15px] object-contain cursor-pointer"
                                    src={getImageUrl("/images/common/white_plus.png")}
                                    alt="Create Form"
                                    title="Create Form"
                                  />
                                )}
                                {canUpdate && (
                                  <img
                                    onClick={() => handleEdit(template)}
                                    className="w-[12px] h-[12px] object-contain cursor-pointer"
                                    src={getImageUrl("/images/common/edit.png")}
                                    alt="Edit"
                                  />
                                )}
                                {canDuplicate && (
                                  <img
                                    onClick={() => handleDuplicate(template)}
                                    className="w-[15px] h-[15px] object-contain cursor-pointer"
                                    src={getImageUrl("/images/common/white_duplicate.png")}
                                    alt="Duplicate"
                                    title="Duplicate"
                                  />
                                )}
                              </>
                            )}
                            {((tab === "Active" && canArchive) || (tab !== "Active" && canRestore)) && (
                              <img
                                onClick={() => requestArchiveRestore(template.id)}
                                className="w-[14px] h-[14px] object-contain cursor-pointer"
                                src={getImageUrl(
                                  tab === "Active"
                                    ? "/images/common/white_archive.png"
                                    : "/images/common/white_repeat.png",
                                )}
                                alt={tab === "Active" ? "Archive" : "Restore"}
                                title={tab === "Active" ? "Archive" : "Restore"}
                              />
                            )}
                          </div>
                        </OutsideClickHandler>
                      )}
                    </td>
                  </tr>
                ))}
                {templates.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-[12px] font-OpenSansRegular text-subTitleColor">
                      No templates found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {hideControls && (canCreate || canUpdate) && (
          <div className="w-full flex items-center justify-center py-4">
            <CreateButton onClicked={openAddTemplateModal} title="ADD TEMPLATE" />
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateList;
